import SCHEMA from '../../schema.json';
import config from '../../config';

class SchemaProcessor {
  constructor(Utils, schema) {
    this.Utils = Utils;
    this.schema = schema;
  }

  unwrapRef(object) {
    if (object.$ref) {
      const propertyChain = object.$ref.split('/').slice(1).join('.');
      return this.Utils.getValueFromPropertyChain(this.schema, propertyChain);
    }

    return object;
  }

  processAccordingToSchema(url, methodArg, data) {
    const debugInfo = config.MARSHALING_WARNINGS_ENABLED ? { url, methodArg } : null;
    const method = methodArg.toLowerCase();

    const allPaths = Object.keys(this.schema.paths);
    let subset = allPaths;
    url.split('/').forEach((urlPart, index) => {
      const matches = subset
        .map((path) => {
          const normalizedPath = path[0] === '/' ? path.slice(1) : path;

          const pathPart = normalizedPath.split('/')[index];

          return {
            isExactMatch: pathPart === urlPart,
            isMatch: /^{.+?}$/.test(pathPart) || pathPart === urlPart,
            path,
          };
        })
        .filter(({ isMatch }) => isMatch);

      const exactMatches = matches.filter(({ isExactMatch }) => isExactMatch);

      if (exactMatches.length) {
        subset = exactMatches.map(({ path }) => path);
      } else {
        subset = matches.map(({ path }) => path);
      }
    });

    const matchingPath = subset[0];

    if (!matchingPath || !this.schema.paths[matchingPath][method]) {
      if (!__TEST__) {
        console.warn('No path matched for', url);
      }

      return data;
    }

    if (!('parameters' in this.schema.paths[matchingPath][method])) {
      _reportUnprocessedData(
        data,
        debugInfo,
        'Parameters key is missing in docs, sending unmarshaled data'
      );
      return data;
    }

    const bodySchema = this.schema.paths[matchingPath][method].parameters.find(
      (p) => p.name === 'body'
    );
    if (!bodySchema) {
      // no body means that the server doesn't expect any payload
      return {};
    }

    const definition = this.unwrapRef(bodySchema.schema);

    if (!definition) {
      return data;
    }

    this.context = {
      path: matchingPath,
      definition,
    };

    return this.transormObjectAccodringToDefinition(definition, data, debugInfo);
  }

  transormObjectAccodringToDefinition(definitionArg, object, debugInfo) {
    const definition = this.unwrapRef(definitionArg);

    if (definition.type === 'array') {
      const mapper = this.transormObjectAccodringToDefinition.bind(this, definition.items);
      return object.map(mapper);
    }

    const transformed = {};

    if (typeof object !== 'object' || !object) {
      return object;
    }

    Object.keys(object).forEach((prop) => {
      if (!('properties' in definition)) {
        // if no properties were specifcally defined then just take'em all
        _reportUnprocessedData(
          object,
          debugInfo,
          'Properties key is missing in docs, sending unmarshaled data'
        );
        transformed[prop] = object[prop];
        return;
      }

      if (prop in definition.properties) {
        const def = this.unwrapRef(definition.properties[prop]);

        if (def.type === 'object') {
          // The most beautiful thing in programming called recursion ❤️❤️❤️❤️❤️
          transformed[prop] = this.transormObjectAccodringToDefinition(def, object[prop]);
        } else if (def.type === 'array') {
          const mapper = this.transormObjectAccodringToDefinition.bind(this, def.items);
          transformed[prop] = Array.isArray(object[prop]) ? object[prop].map(mapper) : object[prop];
        } else {
          transformed[prop] = object[prop];
        }
      }
    });

    return transformed;
  }
}

// This is temporary reporting to pinpoint the requests sending unmarshaled body
function _reportUnprocessedData(data, debugInfo, title) {
  if (config.MARSHALING_WARNINGS_ENABLED && !__TEST__) {
    if (Object.keys(data).length) {
      console.warn(title, debugInfo);
    }
  }
}

export default function MarshalInterceptor(Utils, $injector) {
  'ngInject';

  const schemaProcessor = new SchemaProcessor(Utils, SCHEMA);
  let Authentication = null;

  return {
    request(requestConfig) {
      if (!Authentication) {
        Authentication = $injector.get('Authentication');
      }

      if (!['PUT', 'POST'].includes(requestConfig.method)) {
        return requestConfig;
      }

      let baseUrl = requestConfig.url.split('?')[0];
      if (baseUrl.startsWith(Authentication.baseUrl)) {
        baseUrl = baseUrl.slice(Authentication.baseUrl.length);
      } else {
        return requestConfig;
      }

      try {
        const processed = schemaProcessor.processAccordingToSchema(
          baseUrl,
          requestConfig.method,
          requestConfig.data
        );

        if (!__TEST__ && requestConfig.data !== processed) {
          console.info('Marshalled payload for', requestConfig.url);
        }

        requestConfig.data = processed;
      } catch (e) {
        if (!__TEST__) {
          console.warn(e);
        }
      }

      return requestConfig;
    },
  };
}
